import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useMobile } from 'common-nextjs';
import React, { useState } from 'react';
import ModalTitle from '~/components/Modal/ModalTitle';
import FilterModalBottomButton from '~/components/Search/FilterModal/FilterModalBottomButton';
import UniqueFilteringModal from '~/components/Search/FilterModal/UniqueFilteringModal';
import DetailList from '~/components/Search/FilteringList/DetailList';
import SimpleFilterCheckbox from '~/components/Search/FilteringList/SimpleFilterCheckbox';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';

interface Props {}

const SearchSort: React.FC<Props> = () => {
  const mobile = useMobile();
  const [open, setOpen] = useState(false);
  const { filterState, lander, changeFacetSelection, sorts } =
    useFacetFiltering();
  const defaultSort = sorts?.default_option;

  const selectedSort = filterState.sort || defaultSort;
  const hasDifferentSortSelected = filterState.sort !== defaultSort;

  function onRequestClose() {
    setOpen(false);
  }

  const sortList = (
    <DetailList>
      {sorts?.options?.map(sort => (
        <SimpleFilterCheckbox
          key={sort.slug}
          checked={selectedSort === sort.slug}
          onChange={() => {
            if (sort.slug === defaultSort) {
              changeFacetSelection('sort', null, true, true);
            } else {
              changeFacetSelection('sort', sort.slug, true, true);
            }
          }}
        >
          {sort.name}
        </SimpleFilterCheckbox>
      ))}
    </DetailList>
  );

  if (mobile) {
    return (
      <>
        <button
          className={clsx(
            'flex h-10 items-center gap-2 rounded-full border bg-white px-4 text-sm',
            {
              'border-primary-500': hasDifferentSortSelected,
            },
          )}
          onClick={() => setOpen(true)}
          type="button"
        >
          <span className="font-semibold">Sort</span>
          {hasDifferentSortSelected && (
            <span className="text-gray-500">(1)</span>
          )}
          <ChevronDownIcon className="h-4 w-4" />
        </button>

        <UniqueFilteringModal
          onRequestClose={onRequestClose}
          open={open}
          height="short"
        >
          <ModalTitle className="sticky top-0 z-10 flex rounded-t bg-white px-4 py-3">
            <div className="flex-1 text-left">
              <button
                className="text-sm font-semibold text-green-500"
                onClick={() => {
                  changeFacetSelection('sort', null, true, true);
                  onRequestClose();
                }}
              >
                Default
              </button>
            </div>
            <div className="flex-1 whitespace-nowrap">Sort</div>
            <div className="flex-1 text-right"></div>
          </ModalTitle>

          <div className="flex-1 px-4">{sortList}</div>

          <FilterModalBottomButton onClick={onRequestClose} />
        </UniqueFilteringModal>
      </>
    );
  }

  return (
    <Popover className="relative">
      <PopoverButton
        className={clsx(
          'flex h-10 items-center gap-2 rounded-full border bg-white px-4 text-sm',
          {
            'border-primary-500': hasDifferentSortSelected,
          },
        )}
      >
        <span className="font-semibold">Sort</span>
        {hasDifferentSortSelected && <span className="text-gray-500">(1)</span>}
        <ChevronDownIcon className="h-4 w-4" />
      </PopoverButton>

      <PopoverPanel className="shadow-raised absolute left-0 top-full z-40 mt-2 max-h-80 w-64 overflow-y-auto rounded border bg-white p-4">
        <div className="mb-4 font-semibold">Sort</div>

        {sortList}
      </PopoverPanel>
    </Popover>
  );
};

export default SearchSort;
